import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import type { LinePeriodData } from "@/api/lineScores";
import { getPeriodLabel } from "@/components/general/Dashboard/PeriodData/periodDataUtils";

const LINE_COLORS = {
    1: "var(--color-chart-blue)",
    2: "#10b981",
    3: "#f59e0b",
    4: "var(--color-general-red)",
} as const;

type LineNum = 1 | 2 | 3 | 4;

interface LinePeriodBreakdownChartProps {
    data: LinePeriodData[] | undefined;
    overallLineData?: LinePeriodData | null;
    isLoading?: boolean;
    teamNames?: { chosen: string; opponent: string };
}

export const LinePeriodBreakdownChart = ({ data, overallLineData, isLoading, teamNames }: LinePeriodBreakdownChartProps) => {
    const [selectedLines, setSelectedLines] = useState<LineNum[]>([1, 2, 3, 4]);

    const toggleLine = (lineNumber: LineNum) => {
        setSelectedLines((prev) =>
            prev.includes(lineNumber) ? prev.filter((l) => l !== lineNumber) : [...prev, lineNumber]
        );
    };

    const hasOpponentData = data?.some((p) => p.opponentTeamAverage != null) ?? false;

    const chartData = useMemo(() => {
        if (!data || data.length === 0) return [];
        const periodEntries = data.map((period) => ({
            period: getPeriodLabel(period.periodId),
            line1: period.chosenTeamAverage.lineAvgs.line1.overallTotalAVG,
            line2: period.chosenTeamAverage.lineAvgs.line2.overallTotalAVG,
            line3: period.chosenTeamAverage.lineAvgs.line3.overallTotalAVG,
            line4: period.chosenTeamAverage.lineAvgs.line4.overallTotalAVG,
            line1_opp: period.opponentTeamAverage?.lineAvgs.line1.overallTotalAVG ?? 0,
            line2_opp: period.opponentTeamAverage?.lineAvgs.line2.overallTotalAVG ?? 0,
            line3_opp: period.opponentTeamAverage?.lineAvgs.line3.overallTotalAVG ?? 0,
            line4_opp: period.opponentTeamAverage?.lineAvgs.line4.overallTotalAVG ?? 0,
        }));
        if (overallLineData) {
            periodEntries.push({
                period: "All",
                line1: overallLineData.chosenTeamAverage.lineAvgs.line1.overallTotalAVG,
                line2: overallLineData.chosenTeamAverage.lineAvgs.line2.overallTotalAVG,
                line3: overallLineData.chosenTeamAverage.lineAvgs.line3.overallTotalAVG,
                line4: overallLineData.chosenTeamAverage.lineAvgs.line4.overallTotalAVG,
                line1_opp: overallLineData.opponentTeamAverage?.lineAvgs.line1.overallTotalAVG ?? 0,
                line2_opp: overallLineData.opponentTeamAverage?.lineAvgs.line2.overallTotalAVG ?? 0,
                line3_opp: overallLineData.opponentTeamAverage?.lineAvgs.line3.overallTotalAVG ?? 0,
                line4_opp: overallLineData.opponentTeamAverage?.lineAvgs.line4.overallTotalAVG ?? 0,
            });
        }
        return periodEntries;
    }, [data, overallLineData]);

    if (isLoading) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <div className="text-on-surface-variant">Loading...</div>
            </div>
        );
    }

    if (!chartData || chartData.length === 0) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <div className="text-on-surface-variant">No data available</div>
            </div>
        );
    }

    const chosenLabel = teamNames?.chosen ?? "Your Team";
    const opponentLabel = teamNames?.opponent ?? "Opponent";

    return (
        <div className="flex-1 min-h-0 w-full h-full flex flex-col">
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mb-3 px-2">
                {hasOpponentData && (
                    <span className="text-xs font-bold text-on-surface-variant">{chosenLabel}:</span>
                )}
                {([1, 2, 3, 4] as const).map((lineNum) => (
                    <button
                        key={`chosen-${lineNum}`}
                        type="button"
                        onClick={() => toggleLine(lineNum)}
                        className="flex items-center gap-1.5 cursor-pointer"
                        style={{ opacity: selectedLines.includes(lineNum) ? 1 : 0.3 }}
                    >
                        <div
                            className="w-3.5 h-3.5 rounded-sm flex-shrink-0"
                            style={{ backgroundColor: LINE_COLORS[lineNum] }}
                        />
                        <span className="text-xs font-medium" style={{ color: LINE_COLORS[lineNum] }}>
                            Line {lineNum}
                        </span>
                    </button>
                ))}
                {hasOpponentData && (
                    <>
                        <span className="text-xs font-bold text-on-surface-variant ml-2">{opponentLabel}:</span>
                        {([1, 2, 3, 4] as const).map((lineNum) => (
                            <button
                                key={`opp-${lineNum}`}
                                type="button"
                                onClick={() => toggleLine(lineNum)}
                                className="flex items-center gap-1.5 cursor-pointer"
                                style={{ opacity: selectedLines.includes(lineNum) ? 1 : 0.3 }}
                            >
                                <div
                                    className="w-3.5 h-3.5 rounded-sm flex-shrink-0 bg-transparent"
                                    style={{ border: `2px solid ${LINE_COLORS[lineNum]}` }}
                                />
                                <span className="text-xs font-medium" style={{ color: LINE_COLORS[lineNum] }}>
                                    Line {lineNum}
                                </span>
                            </button>
                        ))}
                    </>
                )}
            </div>

            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }} barCategoryGap="25%" barGap={1}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.15)" vertical={true} />
                    <XAxis
                        dataKey="period"
                        tick={{ fill: "var(--color-chart-axis)", fontSize: 13, fontWeight: 700 }}
                        tickLine={false}
                        axisLine={false}
                    />
                    <YAxis
                        tick={{ fill: "var(--color-chart-axis)", fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                    />
                    <Tooltip
                        cursor={{ fill: "rgba(255,255,255,0.04)" }}
                        wrapperStyle={{ zIndex: 50 }}
                        content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;

                            const byLine = ([1, 2, 3, 4] as const)
                                .filter((n) => selectedLines.includes(n))
                                .map((n) => ({
                                    lineNum: n,
                                    chosen: payload.find((e) => e.dataKey === `line${n}`)?.value,
                                    opp: payload.find((e) => e.dataKey === `line${n}_opp`)?.value,
                                }))
                                .filter((r) => r.chosen !== undefined || r.opp !== undefined);

                            if (byLine.length === 0) return null;

                            return (
                                <div className="chart-tooltip w-48">
                                    <div className="font-semibold text-sm mb-2" style={{ color: "var(--color-chart-blue)" }}>{label}</div>
                                    <div className="grid grid-cols-[auto_1fr_1fr] gap-x-3 gap-y-1 items-center">
                                        <div />
                                        <div className="text-[10px] font-bold text-on-surface-variant text-center truncate">{chosenLabel}</div>
                                        <div className="text-[10px] font-bold text-on-surface-variant text-center truncate">{opponentLabel}</div>
                                        {byLine.map(({ lineNum, chosen, opp }) => {
                                            const color = LINE_COLORS[lineNum];
                                            return [
                                                <div key={`lbl-${lineNum}`} className="flex items-center gap-1.5">
                                                    <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
                                                    <span className="text-xs text-on-surface-variant">L{lineNum}</span>
                                                </div>,
                                                <div key={`c-${lineNum}`} className="text-center font-bold text-sm" style={{ color }}>{chosen ?? "—"}</div>,
                                                <div key={`o-${lineNum}`} className="text-center font-bold text-sm" style={{ color }}>{opp ?? "—"}</div>,
                                            ];
                                        })}
                                    </div>
                                </div>
                            );
                        }}
                    />
                    {([1, 2, 3, 4] as const).flatMap((lineNum) => [
                        <Bar
                            key={`line${lineNum}`}
                            dataKey={`line${lineNum}`}
                            name={`Line ${lineNum}`}
                            fill={LINE_COLORS[lineNum]}
                            fillOpacity={selectedLines.includes(lineNum) ? 1 : 0.1}
                            radius={[3, 3, 0, 0]}
                            isAnimationActive={false}
                        />,
                        ...(hasOpponentData ? [
                            <Bar
                                key={`line${lineNum}_opp`}
                                dataKey={`line${lineNum}_opp`}
                                name={`Line ${lineNum} (opp)`}
                                fill="transparent"
                                stroke={LINE_COLORS[lineNum]}
                                strokeWidth={2}
                                fillOpacity={selectedLines.includes(lineNum) ? 1 : 0.1}
                                strokeOpacity={selectedLines.includes(lineNum) ? 1 : 0.1}
                                radius={[3, 3, 0, 0]}
                                isAnimationActive={false}
                            />,
                        ] : []),
                    ])}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};
